import { CONFIGS, useConfigQuery } from '@buildwise/libs/configurations';
import { BUILDWISE_MODULES, BUILDWISE_MODULES_FEATURES } from 'components/drawer/constants';
import { ConfigEntryFeatureType, ConfigEntryType } from 'config/config';
import useUserData from 'hooks/useUserData';
import pluralize, { singular } from 'pluralize';
import { useMemo } from 'react';

export enum HomeModuleFeatures {
  SHARE_KNOWLEDGE = 'Share Knowledge',
  ASK_PEERS = 'Ask Peers',
  SUGGEST_IMPROVEMENTS = 'Suggest Improvements',
  REQUEST_A_POST = 'Request a Post',
  CHAT_WITH_AI = 'Chat with AI',
  SUMMARIZE_SEARCH_RESULTS = 'Summarize Search Results',
  SUMMARIZE_LEARNINGS = 'Summarize Learnings',
  SUMMARIZE_FORM_WITH_AI = 'Summarize with AI',
  MAKE_A_POST = 'MAKE_A_POST',
  NOTES = 'Notes',
}
const ARTICLE_BUTTONS: Record<string, ConfigEntryFeatureType> = {
  [HomeModuleFeatures.SHARE_KNOWLEDGE]: {
    displayName: 'Share Knowledge',
    name: HomeModuleFeatures.SHARE_KNOWLEDGE,
    isActive: true,
  },
  [HomeModuleFeatures.ASK_PEERS]: {
    displayName: 'Ask Peers',
    name: HomeModuleFeatures.ASK_PEERS,
    isActive: true,
  },
  [HomeModuleFeatures.SUGGEST_IMPROVEMENTS]: {
    displayName: 'Suggest Improvements',
    name: HomeModuleFeatures.SUGGEST_IMPROVEMENTS,
    isActive: true,
  },
  [HomeModuleFeatures.REQUEST_A_POST]: {
    displayName: 'Request a Post',
    name: HomeModuleFeatures.REQUEST_A_POST,
    isActive: true,
  },
  [HomeModuleFeatures.CHAT_WITH_AI]: {
    displayName: 'Chat with AI',
    name: HomeModuleFeatures.CHAT_WITH_AI,
    isActive: true,
  },
  [HomeModuleFeatures.SUMMARIZE_SEARCH_RESULTS]: {
    displayName: 'Summarize these posts',
    name: HomeModuleFeatures.SUMMARIZE_SEARCH_RESULTS,
    isActive: true,
  },
  [HomeModuleFeatures.SUMMARIZE_LEARNINGS]: {
    displayName: 'Summarize these posts',
    name: HomeModuleFeatures.SUMMARIZE_SEARCH_RESULTS,
    isActive: true,
  },
  [HomeModuleFeatures.SUMMARIZE_FORM_WITH_AI]: {
    displayName: 'Summarize with AI',
    name: HomeModuleFeatures.SUMMARIZE_FORM_WITH_AI,
    isActive: true,
  },
  [HomeModuleFeatures.MAKE_A_POST]: {
    displayName: 'Make this a Post',
    name: HomeModuleFeatures.MAKE_A_POST,
    isActive: true,
  },
  [HomeModuleFeatures.NOTES]: {
    displayName: 'NOTES',
    name: HomeModuleFeatures.NOTES,
    isActive: true,
  },
  [BUILDWISE_MODULES_FEATURES.HOME.WRITE_MANUALLY]: {
    displayName: 'Write it Yourself',
    name: BUILDWISE_MODULES_FEATURES.HOME.WRITE_MANUALLY,
    isActive: true,
  },
  [BUILDWISE_MODULES_FEATURES.HOME.WRITE_WITH_AI]: {
    displayName: 'Write with AI',
    name: BUILDWISE_MODULES_FEATURES.HOME.WRITE_WITH_AI,
    isActive: true,
  },
  [BUILDWISE_MODULES_FEATURES.HOME.PARSE_FILE]: {
    displayName: 'Summarize from File',
    name: BUILDWISE_MODULES_FEATURES.HOME.PARSE_FILE,
    isActive: true,
  },
  [BUILDWISE_MODULES_FEATURES.HOME.WE_WILL_WRITE]: {
    displayName: "We'll Write It For You",
    name: BUILDWISE_MODULES_FEATURES.HOME.WE_WILL_WRITE,
    isActive: true,
  },
};

function useBuildWiseModules() {
  const { companyId, company } = useUserData({
    shouldFetchCompanyDetails: false,
    shouldFetchUserDetails: false,
  });
  const {
    entries: buildwiseModules,
    config: buildwiseModulesConfig,
    isLoading: isLoadingBuildWiseModules,
    isFetched: isFetchedBuildWiseModules,
    updateConfigCache,
  } = useConfigQuery({
    companyId,
    configName: CONFIGS.BUILDWISE_MODULES.configName,
    shouldFetch: !!company,
  });
  const {
    homeModule,
    homeModuleName,
    homeModuleFeatures,
    partnersModuleName,
    projectsModuleName,
    proceduresModuleName,
    insightsModule,
    insightsModuleName,
    retrospectivesModule,
    retrospectivesModuleName,
    projectsModule,
    projectModuleFeatures,
    partnersModule,
    partnersModuleFeatures,
    proceduresModule,
    helpCenterModule,
    helpCenterModuleName,
    collectionsModule,
    collectionsModuleName,
    trackersModule,
    trackersModuleName,
    trackersModuleFeatures,
  } = useMemo(() => {
    let homeModule: ConfigEntryType | undefined;
    let retrospectivesModule: ConfigEntryType | undefined;
    let insightsModule: ConfigEntryType | undefined;
    let projectsModule: ConfigEntryType | undefined;
    let partnersModule: ConfigEntryType | undefined;
    let proceduresModule: ConfigEntryType | undefined;
    let helpCenterModule: ConfigEntryType | undefined;
    let collectionsModule: ConfigEntryType | undefined;
    let trackersModule: ConfigEntryType | undefined; // <-- add trackers module variable

    buildwiseModules.forEach((module) => {
      if (module.entryCode === BUILDWISE_MODULES.HOME) {
        homeModule = module;
      }
      if (module.entryCode === BUILDWISE_MODULES.RETROSPECTIVES) {
        retrospectivesModule = module;
      }
      if (module.entryCode === BUILDWISE_MODULES.INSIGHTS) {
        insightsModule = module;
      }
      if (module.entryCode === BUILDWISE_MODULES.PROJECTS) {
        projectsModule = module;
      }
      if (module.entryCode === BUILDWISE_MODULES.PARTNERS) {
        partnersModule = module;
      }
      if (module.entryCode === BUILDWISE_MODULES.PROCEDURES) {
        proceduresModule = module;
      }
      if (module.entryCode === BUILDWISE_MODULES.HELP_CENTER) {
        helpCenterModule = module;
      }
      if (module.entryCode === BUILDWISE_MODULES.COLLECTIONS) {
        collectionsModule = module;
      }
      if (module.entryCode === BUILDWISE_MODULES.TRACKERS) {
        trackersModule = module;
      }
    });
    const homeModuleFeatures: Record<string, ConfigEntryFeatureType> = {
      ...ARTICLE_BUTTONS,
    };
    homeModule?.referenceObject?.features?.forEach(({ name, ...item }) => {
      if (homeModuleFeatures[name]) {
        homeModuleFeatures[name] = {
          ...homeModuleFeatures[name],
          ...item,
        };
      } else {
        homeModuleFeatures[name] = {
          name,
          ...item,
        };
      }
    });
    const projectModuleFeatures: Record<string, ConfigEntryFeatureType> = Object.fromEntries(
      projectsModule?.referenceObject?.features?.map((item) => [item.name, item]) || [],
    );
    const partnersModuleFeatures: Record<string, ConfigEntryFeatureType> = Object.fromEntries(
      partnersModule?.referenceObject?.features?.map((item) => [item.name, item]) || [],
    );
    const trackersModuleFeatures: Record<string, ConfigEntryFeatureType> = Object.fromEntries(
      trackersModule?.referenceObject?.features?.map((item) => [item.name, item]) || [],
    );

    return {
      homeModule,
      homeModuleName: homeModule?.entryDescription || BUILDWISE_MODULES.HOME,
      homeModuleFeatures,
      partnersModuleName:
        buildwiseModules.find((module) => module.entryCode === BUILDWISE_MODULES.PARTNERS)
          ?.entryDescription || BUILDWISE_MODULES.PARTNERS,
      projectsModuleName:
        buildwiseModules.find((module) => module.entryCode === BUILDWISE_MODULES.PROJECTS)
          ?.entryDescription || BUILDWISE_MODULES.PROJECTS,
      proceduresModuleName:
        buildwiseModules.find((module) => module.entryCode === BUILDWISE_MODULES.PROCEDURES)
          ?.entryDescription || BUILDWISE_MODULES.PROCEDURES,
      insightsModule,
      insightsModuleName:
        buildwiseModules.find((module) => module.entryCode === BUILDWISE_MODULES.INSIGHTS)
          ?.entryDescription || BUILDWISE_MODULES.INSIGHTS,
      retrospectivesModule,
      retrospectivesModuleName:
        buildwiseModules.find((module) => module.entryCode === BUILDWISE_MODULES.RETROSPECTIVES)
          ?.entryDescription || BUILDWISE_MODULES.RETROSPECTIVES,
      projectsModule,
      projectModuleFeatures,
      partnersModule,
      partnersModuleFeatures,
      proceduresModule,
      helpCenterModule,
      helpCenterModuleName: helpCenterModule?.entryDescription || BUILDWISE_MODULES.HELP_CENTER,
      collectionsModule,
      collectionsModuleName: collectionsModule?.entryDescription || BUILDWISE_MODULES.COLLECTIONS,
      trackersModule,
      trackersModuleName: trackersModule?.entryDescription || BUILDWISE_MODULES.TRACKERS,
      trackersModuleFeatures,
    };
  }, [buildwiseModules]);

  return {
    isFetchedBuildWiseModules,
    isLoadingBuildWiseModules,
    buildwiseModules,
    buildwiseModulesConfig,
    homeModule,
    homeModuleName,
    homeModuleFeatures,
    singularHomeModuleName: singular(homeModuleName),
    pluralHomeModuleName: pluralize(homeModuleName),
    projectsModuleName,
    singularProjectsModuleName: singular(projectsModuleName),
    pluralProjectsModuleName: pluralize(projectsModuleName),
    partnersModuleName,
    singularPartnersModuleName: singular(partnersModuleName),
    pluralPartnersModuleName: pluralize(partnersModuleName),
    proceduresModuleName,
    singularProceduresModuleName: singular(proceduresModuleName),
    pluralProceduresModuleName: pluralize(proceduresModuleName),
    insightsModule,
    insightsModuleName,
    singularInsightsModuleName: singular(insightsModuleName),
    pluralInsightsModuleName: pluralize(insightsModuleName),
    retrospectivesModule,
    retrospectivesModuleName,
    projectsModule,
    projectModuleFeatures,
    partnersModule,
    partnersModuleFeatures,
    proceduresModule,
    helpCenterModule,
    helpCenterModuleName,
    singularHelpCenterModuleName: singular(helpCenterModuleName),
    pluralHelpCenterModuleName: pluralize(helpCenterModuleName),
    singularRetrospectivesModuleName: singular(retrospectivesModuleName),
    pluralRetrospectivesModuleName: pluralize(retrospectivesModuleName),
    collectionsModule,
    collectionsModuleName,
    singularCollectionsModuleName: singular(collectionsModuleName),
    pluralCollectionsModuleName: pluralize(collectionsModuleName),
    trackersModule,
    trackersModuleName,
    trackersModuleFeatures,
    singularTrackersModuleName: singular(trackersModuleName),
    pluralTrackersModuleName: pluralize(trackersModuleName),
    updateConfigCache,
  };
}

export default useBuildWiseModules;
